/**
 * render.ts — the results panel (P1 US-114, FR-25, FR-26).
 *
 * Ported from oracle lines 880–893 (`barres`), 1031–1053 (`blocELS`) and 1054–1076 (the panel
 * composition inside `calculer`).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * STATUS COMES FROM THE ENVELOPE. IT IS NOT RECOMPUTED HERE.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * Each check arrives with a `status` the server already derived, using thresholds transcribed from
 * the oracle's `niveau()`. Recomputing them in the browser would create a THIRD copy of
 * `t > 1 ? 'ko' : t > 0.85 ? 'warn' : 'ok'` — the engine's adapter and the contract already hold
 * two, deliberately, because the engine may not depend on the contract, and
 * `contract-roundtrip.test.ts` is what holds those two together. A third copy would have nothing
 * holding it, and the way it would fail is a row rendered green whose ratio the server called `ko`.
 *
 * Where the UI genuinely needs a level the envelope does not carry — the global banner and the
 * note's conclusion, both of which combine ULS and SLS into one number the API never computes —
 * it imports `checkStatusFromRatio` from the contract rather than transcribing the thresholds.
 */

import {
  checkStatusFromRatio,
  type CalcResponse,
  type TimberDeflectionBlock,
} from '@ronceray/contract';

import { detailsFor, type Values } from './details';
import { fmt, ratio3 } from './format';

/** A row the bar table can render: an envelope check, or an ELS row synthesised below. */
export interface BarRow {
  readonly label: string;
  readonly eq: string;
  readonly ratio: number | null;
  /** Absent for a synthesised ELS row, which has no server-assigned status. */
  readonly status?: string;
}

/**
 * The utilisation bar's three magic numbers, from oracle line 886 and the stylesheet's
 * `.bar u { left: 74.07% }`.
 *
 * The fill **saturates at 1.35**: a ratio of 1.35 fills the bar completely, and anything above it
 * — 2, 10, 400 — fills it exactly the same. That is a deliberate readability choice, not a bug. An
 * unbounded scale would compress every interesting value into the first few pixels the moment one
 * check blew up, and the interesting range in a verification tool is 0.8 to 1.2.
 *
 * The tick sits at `1 / 1.35 = 74.07%`, which is the 1.00 mark. The two numbers are one decision
 * expressed in two files; move either without the other and every bar in the tool lies about where
 * the limit is. `styles.css` holds the tick and `test/render.test.ts` asserts they still agree.
 */
export const BAR_SATURATION = 1.35;

/** Fill width, in percent, for a ratio. Oracle line 886. */
export function barWidth(ratio: number): number {
  return (Math.min(ratio, BAR_SATURATION) / BAR_SATURATION) * 100;
}

/**
 * The checks table. Oracle lines 880–893.
 *
 * A row whose ratio is null or non-finite renders as **not applicable** — a dash, an empty bar
 * cell, and the words `non applicable` — rather than as a zero. That distinction matters more here
 * than almost anywhere else in the UI: `DEVIATIONS.md` D-001 records that the engine has no concept
 * of a check that could not be evaluated, so a check silently vanishing from `max` is a known,
 * frozen defect. Rendering it as `0.000` in green would hide exactly the case the engineer needs
 * to see.
 */
export function barres(rows: readonly BarRow[]): string {
  let h =
    '<table class="checks"><thead><tr><th>Verification</th><th>Eq.</th><th>Taux</th>' +
    '<th class="bar-col">Utilisation</th><th>Statut</th></tr></thead><tbody>';

  for (const c of rows) {
    if (c.ratio === null || !Number.isFinite(c.ratio)) {
      h +=
        `<tr class="na"><td>${c.label}</td><td>${c.eq}</td><td>-</td>` +
        '<td class="bar-col"></td><td>non applicable</td></tr>';
      continue;
    }

    // The envelope's status when there is one; the contract's classifier for a synthesised ELS
    // row. Never a threshold written in this file.
    const level = c.status ?? checkStatusFromRatio(c.ratio);
    const width = barWidth(c.ratio);

    h +=
      `<tr class="${level}"><td>${c.label}</td><td>${c.eq}</td>` +
      `<td class="num">${ratio3(c.ratio)}</td>` +
      `<td class="bar-col"><div class="bar"><i style="width:${width.toFixed(1)}%"></i><u></u></div></td>` +
      `<td class="statut">${c.ratio <= 1 ? 'OK' : 'NON VERIFIE'}</td></tr>`;
  }

  return `${h}</tbody></table>`;
}

/**
 * Is this a timber deflection block?
 *
 * The oracle branches on `r.type`, which the envelope does not carry — the response shape is the
 * same for all three modules except here. `'taux' in els` is the contract's own documented
 * discriminator: `taux` exists only on timber, `tauxTot`/`tauxQ` only on steel. Typed as a
 * predicate over the union member so the `else` branch narrows to steel rather than staying wide.
 */
function isTimberEls(els: CalcResponse['els']): els is TimberDeflectionBlock {
  return 'taux' in els;
}

/**
 * The serviceability block. Oracle lines 1031–1053.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THE ELS BARS ARE SYNTHESISED HERE, AND MUST NEVER BE ADDED TO `checks[]`
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * The envelope's `checks[]` is **ULS only** and its 37 ids are frozen. The one or two
 * serviceability rows below are built by this renderer out of `els`, exactly as the oracle builds
 * them — they have no ids, and they are not verifications the engine emitted.
 *
 * Moving them into `checks[]` would look like a tidy-up and would break four things at once: the
 * id register's bijection with the fixtures, the 12 / 15 / 10 count assertions, the golden parity
 * test, and every stored case whose `checks` array a P2 recalculation has to align against.
 */
export function blocELS(els: CalcResponse['els']): string {
  let h = '<table class="vals"><tbody>';

  const row = (label: string, sym: string, value: unknown): string =>
    `<tr><td>${label}</td><td class="sym">${sym}</td><td class="num">${fmt(value, 2)}</td><td class="u">mm</td></tr>`;

  if (isTimberEls(els)) {
    h += row('Fleche instantanee totale', 'w,inst', els.winst);
    h += row('Fleche finale avec fluage', 'w,fin', els.wfin);
    h += row('Fleche admissible', 'w,lim', els.wlim);
    return (
      `${h}</tbody></table>` + barres([{ label: 'Fleche finale', eq: '(7.2)', ratio: els.taux }])
    );
  }

  h += row('Fleche sous G', 'w,G', els.wG);
  h += row('Fleche sous Q', 'w,Q', els.wQ);
  h += row('Fleche resultante', 'w,tot', els.wtot);
  h += row('Fleche admissible totale', 'w,lim', els.wlim);
  h += row('Fleche admissible sous Q', 'w,lim,Q', els.wlimQ);

  return (
    `${h}</tbody></table>` +
    barres([
      { label: 'Fleche totale G + Q', eq: 'ELS', ratio: els.tauxTot },
      { label: 'Fleche sous Q seule', eq: 'ELS', ratio: els.tauxQ },
    ])
  );
}

/**
 * The governing utilisation shown in the banner: the worse of ULS and SLS.
 *
 * `max` is ULS ONLY — the contract says so explicitly, and redefining it to include serviceability
 * would silently change every stored result. So the combination happens here, exactly as the
 * oracle does it at line 1063, `|| 0` included: a null `maxELS` contributes nothing rather than
 * poisoning the maximum with `NaN`.
 */
export function globalRatio(envelope: CalcResponse): number {
  return Math.max(envelope.max, envelope.els.maxELS ?? 0);
}

/** The section designation in the banner. Oracle line 1065. */
export function titreElement(
  module: 'lamine' | 'creux' | 'bois',
  input: Record<string, unknown>,
): string {
  if (module === 'lamine') return String(input['profil'] ?? '');
  if (module === 'creux') {
    return input['section'] === 'Personnalise'
      ? `${String(input['hp'])}x${String(input['bp'])}x${String(input['tp'])}`
      : String(input['section'] ?? '');
  }
  return `${String(input['classe'])} ${String(input['b'])}x${String(input['h'])}`;
}

/**
 * The whole results panel. Oracle lines 1064–1075.
 *
 * Composition order is part of the port: banner, class-4 alert, ULS heading and table, SLS heading
 * and block, then the collapsed details. An engineer comparing the beta against the file they use
 * today reads down the panel; a reordered panel is a diff even when every number matches.
 */
export function panelHTML(
  module: 'lamine' | 'creux' | 'bois',
  input: Record<string, unknown>,
  envelope: CalcResponse,
): string {
  const glob = globalRatio(envelope);
  const level = checkStatusFromRatio(glob);

  let h = '';

  h +=
    `<div class="banner ${level}"><div class="b-left">` +
    `<span class="b-sec">${titreElement(module, input)}</span>` +
    `<span class="b-txt">${glob <= 1 ? 'SECTION VERIFIEE' : 'SECTION NON VERIFIEE'}</span></div>` +
    `<div class="b-right"><span class="b-num">${ratio3(glob)}</span>` +
    '<span class="b-lab">taux maxi</span></div></div>';

  // Oracle line 1070. `|| 1` is the oracle's own guard for a bag with no `classe` key — timber,
  // which has no section classification at all, so the alert can never fire for it.
  const classe = Number(envelope.values['classe'] ?? 1);
  if (classe >= 4) {
    h +=
      '<p class="alerte">Section de CLASSE 4 : hors domaine de la feuille, section efficace a ' +
      'determiner selon EN 1993-1-5.</p>';
  }

  h += `<h3>Verifications ELU</h3>${barres(envelope.checks)}`;
  h += `<h3>Etat limite de service</h3>${blocELS(envelope.els)}`;
  h +=
    '<details class="det"><summary>Valeurs intermediaires detaillees</summary>' +
    `${detailsFor(module, envelope.values as Values)}</details>`;

  return h;
}

/**
 * The panel's non-result states.
 *
 * The oracle had exactly one: `Saisie incomplete.` (line 1060). The beta needs more, because a
 * network failure and an invalid input call for completely different actions from the user, and a
 * single generic message would leave an engineer refreshing the page over a typo. They share the
 * oracle's `p.vide` styling so they stay visually quiet — this is a tool, not a product tour.
 */
export function messageHTML(message: string): string {
  return `<p class="vide">${message}</p>`;
}
